import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from 'antd';

import { useUserStore } from '@store/app.store';
import { useSetDocumentTitle, useStyledToast } from '@util/hooks';
import { Spacer, Stack, HStack, Button, Heading, Tooltip, Text, Link } from '@chakra-ui/react';
import { SFPlusIcon } from '@assets/icons';
import { checkLicenseLimitForActivation } from '@util/helpers/person.helpers';
import { getStripeCustomerPortalSession } from '@services/query-services/lambda.query-service';

import { tableParamsToBody } from '@util/helpers/table.helpers.jsx';
import { usePeople } from '@services/query-services/people.query-service';
import { useTenantById } from '@services/query-services/tenants.query-service';
import { filtersToUserSearchBody, useUserColumns } from '@util/helpers';
import './manage-users.css';

function ManageUsersPage() {
  useSetDocumentTitle('Manage Users - FORA');
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const appTenant = useUserStore((state) => state.tenant); // Keep this for tenant ID
  const isEnterpriseUser = useUserStore((state) => state.isEnterpriseUser);
  const toast = useStyledToast();
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [peopleArr, setPeopleArr] = useState([]);
  const columns = useUserColumns();

  // Fetch fresh tenant data from backend for license checking
  const { data: freshTenant } = useTenantById(appTenant?.id);

  // Additional query for active users count to check license limits
  const { data: activePeopleData } = usePeople(
    { pageSize: 1000, active: true, tenant_ids: [appTenant?.id] },
    { includeTotal: true }
  );

  const currentActiveCount = activePeopleData?.total || 0;
  const licenseCheck = checkLicenseLimitForActivation({
    tenant: freshTenant || appTenant, // Use fresh tenant data when available, fallback to cached
    currentActiveCount,
    isEnterpriseUser
  });

  const { isActiveSwitchDisabled: isNewUserDisabled, licenseLimit } = licenseCheck;

  // Handle Stripe customer portal navigation
  const handleManageSubscription = async () => {
    setIsStripeLoading(true);
    try {
      const session = await getStripeCustomerPortalSession(
        user.tenants[0].stripe_config.customer_id
      );
      window.location.href = session.url;
    } catch (e) {
      setIsStripeLoading(false);
      toast({
        title: 'Error accessing billing portal',
        description: 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 20
    }
  });

  const {
    data: peopleRes,
    isLoading,
    refetch
  } = usePeople(tableParamsToBody(tableParams, filtersToUserSearchBody), { includeTotal: true });

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({ pagination, filters, sorter });
  };

  useEffect(() => {
    refetch();
  }, [tableParams]);

  useEffect(() => {
    let filteredPeople = peopleRes?.data;
    if (appTenant?.id !== '8cff9346-a5fe-42e6-bd20-de3731fe3bfe') {
      filteredPeople = peopleRes?.data?.filter(
        (person) => person.email.includes('@fora.day') === false
      );
    }
    setPeopleArr(filteredPeople);
    setTotal(peopleRes?.total);
  }, [peopleRes?.data]);

  return (
    <Stack
      p={11}
      gap={11}
    >
      <HStack gap={4}>
        <Heading
          size={{ base: 'sm', lg: '2xl' }}
          lineHeight={1.3}
        >
          Manage Users
        </Heading>
        <Spacer />
        <Tooltip
          label={
            isNewUserDisabled
              ? `License limit reached (${currentActiveCount}/${licenseLimit}). Cannot create more users.`
              : ''
          }
          isDisabled={!isNewUserDisabled}
          data-testid='license-limit-tooltip'
        >
          <Button
            onClick={() => navigate('/settings/manage-users/add-user')}
            size='reskinMd'
            variant='reskinPrimary'
            iconSpacing={1}
            rightIcon={<SFPlusIcon style={{ paddingRight: 0 }} />}
            isDisabled={isNewUserDisabled}
            data-testid='new-user-button'
          >
            New User
          </Button>
        </Tooltip>
      </HStack>

      {/* License information helper text - only for non-Enterprise users */}
      {!isEnterpriseUser() && (
        <Stack
          gap={2}
          data-testid='license-limit-text'
        >
          <Text
            fontSize='sm'
            color='surface.onSurfaceVariant'
          >
            You have used {currentActiveCount} of your {licenseLimit} available licenses.
          </Text>
          <Text
            fontSize='sm'
            color='surface.onSurfaceVariant'
            data-testid='manage-subscription-text'
          >
            If you want more licenses,{' '}
            <Link
              color='primary.main'
              textDecoration='underline'
              onClick={handleManageSubscription}
              isDisabled={isStripeLoading}
              cursor={isStripeLoading ? 'not-allowed' : 'pointer'}
            >
              {isStripeLoading ? 'Loading...' : 'click here'}
            </Link>{' '}
            to manage your subscription.
          </Text>
        </Stack>
      )}

      <Table
        className='manage-users-table'
        size='small'
        rowKey='id'
        loading={isLoading}
        columns={columns}
        dataSource={peopleArr}
        pagination={{ ...tableParams.pagination, total }}
        onChange={handleTableChange}
        onRow={(record) => {
          return {
            onClick: () => navigate(`/settings/manage-users/edit-user?id=${record?.id}`)
          };
        }}
      />
    </Stack>
  );
}

export default ManageUsersPage;
